// "Mark box empty": the shelf said a box held parts and the bench found none.
// Every item in the box is written down to zero, with the note saying what was
// found. Opened beside the scan field carrying whatever barcode is typed there,
// or from a box's own row in Already picked.

import React, { useEffect, useRef, useState } from 'react';
import Popover from './Popover';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import { Field, TextArea } from '../components/ui/Field';
import { useToast } from '../components/Toast/ToastContext';
import { useWorkOrdersApi, workOrderErrorMessage } from './workOrdersApi';

interface Props {
  /** The barcode the form opens with. */
  barcode: string;
  triggerLabel?: string;
  triggerAriaLabel?: string;
  triggerClassName: string;
  disabled?: boolean;
  onDone: () => void;
}

const BoxEmptyDialog: React.FC<Props> = ({
  barcode,
  triggerLabel = 'Mark box empty',
  triggerAriaLabel,
  triggerClassName,
  disabled,
  onDone,
}) => (
  <Popover
    triggerLabel={triggerLabel}
    triggerAriaLabel={triggerAriaLabel}
    triggerClassName={triggerClassName}
    tooltip="The box is empty but the shelf count says otherwise. Writes its count down to zero."
    title="Mark a box empty"
    align="right"
    disabled={disabled}
  >
    {(close) => (
      <EmptyForm
        initialBarcode={barcode}
        onCancel={close}
        onDone={() => {
          onDone();
          close();
        }}
      />
    )}
  </Popover>
);

const EmptyForm: React.FC<{
  initialBarcode: string;
  onCancel: () => void;
  onDone: () => void;
}> = ({ initialBarcode, onCancel, onDone }) => {
  const woApi = useWorkOrdersApi();
  const toast = useToast();
  const [barcode, setBarcode] = useState(initialBarcode.trim());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const barcodeRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // The popover focuses its first field; the box's note is the one to type when the barcode came pre-filled.
  useEffect(() => {
    (initialBarcode.trim() ? noteRef : barcodeRef).current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    const code = barcode.trim();
    if (!code) {
      setError('Scan or type the box barcode.');
      return;
    }
    if (!note.trim()) {
      setError('Say what you found, so the count can be checked later.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await woApi.assemblyBoxEmpty({ barcode: code }, note.trim());
      toast.success(`Marked ${code} empty. Its count is now zero.`);
      onDone();
    } catch (err) {
      setError(workOrderErrorMessage(err, 'The box did not change. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">Box barcode</span>
        <Field
          type="text"
          autoComplete="off"
          spellCheck={false}
          ref={barcodeRef}
          value={barcode}
          onChange={(e) => {
            setBarcode(e.target.value);
            setError(null);
          }}
          className="font-mono"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">What did you find?</span>
        <TextArea
          rows={2}
          value={note}
          maxLength={1000}
          ref={noteRef}
          placeholder="Box was empty on the shelf"
          onChange={(e) => {
            setNote(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
      </label>
      <Alert tone="error" variant="text" show={!!error}>{error}</Alert>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className={buttonClass('quiet', 'md')}>
          Never mind
        </button>
        <button
          type="submit"
          disabled={busy}
          className={buttonClass('danger', 'md')}
        >
          {busy ? 'Saving…' : 'Mark empty'}
        </button>
      </div>
    </form>
  );
};

export default BoxEmptyDialog;
